/**
 * API endpoint definitions for the entire application.
 * This ensures that both frontend and backend use consistent endpoint paths.
 */
export const API_VERSION = "v1";
export const Endpoints = {
    // Auth endpoints
    auth: {
        login: `/api/${API_VERSION}/auth/login`,
        logout: `/api/${API_VERSION}/auth/logout`,
        refreshToken: `/api/${API_VERSION}/auth/refresh`,
        register: `/api/${API_VERSION}/auth/register`,
        sendPasswordUpdate: `/api/${API_VERSION}/auth/send-password-update`,
        checkEmail: `/api/${API_VERSION}/auth/check-email`,
        checkCompanyName: `/api/${API_VERSION}/auth/checkCompanyName`,
        createVerificationEmail: `/api/${API_VERSION}/auth/create-verification-email`,
        acceptEmailVerification: `/api/${API_VERSION}/auth/accept-email-verification`,
        acceptPasswordReset: `/api/${API_VERSION}/auth/accept-password-reset`,
        sendPasswordResetRequest: `/api/${API_VERSION}/auth/send-password-reset-request`,
    },
    // Account endpoints
    account: {
        getAccountList: `/api/${API_VERSION}/account/list`,
        getAccountById: (accountId) => `/api/${API_VERSION}/account/details/${accountId}`,
        getAdminList: `/api/${API_VERSION}/account/admin-list`,
        getWalkerList: `/api/${API_VERSION}/account/walker-list`,
        update: `/api/${API_VERSION}/account/update`,
        updateSelf: `/api/${API_VERSION}/account/update-self`,
        createAdmin: `/api/${API_VERSION}/account/create-admin`,
        create: `/api/${API_VERSION}/account/create`,
        reviveAccount: `/api/${API_VERSION}/account/revive`,
        updateNotificationPreferences: `/api/${API_VERSION}/account/notification-preferences`,
        updateEvaluator: `/api/${API_VERSION}/account/update-evaluator`,
        addCompanyToAccount: `/api/${API_VERSION}/account/add-company`,
        removeCompanyFromAccount: `/api/${API_VERSION}/account/remove-company`,
        deleteAccount: `/api/${API_VERSION}/account/delete-account`,
    },
    // Criteria catalog versions (Admin-Bereich "Katalog")
    catalogVersion: {
        list: `/api/${API_VERSION}/catalog-versions/list`,
        getSpecific: `/api/${API_VERSION}/catalog-versions/specific`,
        duplicate: `/api/${API_VERSION}/catalog-versions/duplicate`,
        update: `/api/${API_VERSION}/catalog-versions/update`,
        delete: `/api/${API_VERSION}/catalog-versions/delete`,
    },
    catalogItem: {
        getList: `/api/${API_VERSION}/catalog-items/list`,
        getSpecific: `/api/${API_VERSION}/catalog-items/specific`,
        create: `/api/${API_VERSION}/catalog-items/create`,
        update: `/api/${API_VERSION}/catalog-items/update`,
        remove: `/api/${API_VERSION}/catalog-items/remove`,
        connectRequirement: `/api/${API_VERSION}/catalog-items/connect-requirement`,
        disconnectRequirement: `/api/${API_VERSION}/catalog-items/disconnect-requirement`,
        fieldSalesExclusion: `/api/${API_VERSION}/catalog-items/field-sales-exclusion`,
    },
    requirement: {
        getList: `/api/${API_VERSION}/requirements/list`,
        getSpecific: `/api/${API_VERSION}/requirements/specific`,
        create: `/api/${API_VERSION}/requirements/create`,
        update: `/api/${API_VERSION}/requirements/update`,
        remove: `/api/${API_VERSION}/requirements/remove`,
    },
    occupation: {
        getList: `/api/${API_VERSION}/occupations/list`,
        getSpecific: `/api/${API_VERSION}/occupations/specific`,
        create: `/api/${API_VERSION}/occupations/create`,
        update: `/api/${API_VERSION}/occupations/update`,
        remove: `/api/${API_VERSION}/occupations/remove`,
    },
    substitute: {
        create: `/api/${API_VERSION}/substitute/create`,
        update: (id) => `/api/${API_VERSION}/substitute/${id}`,
        delete: (id) => `/api/${API_VERSION}/substitute/${id}`,
        list: `/api/${API_VERSION}/substitute/list`,
    },
    // Action List endpoints
    actionList: {
        getActionList: `/api/${API_VERSION}/action-list`,
        getCompletedActionList: `/api/${API_VERSION}/action-list/completed`,
        getAccountActionList: (accountId) => `/api/${API_VERSION}/action-list/account/${accountId}`,
        getById: (id) => `/api/${API_VERSION}/action-list/specific/${id}`,
        getActionListItem: `/api/${API_VERSION}/action-list/item`,
        attachActionListItemToAdmin: `/api/${API_VERSION}/action-list/attach`,
        attachActionListArrayToAdmin: `/api/${API_VERSION}/action-list/attach-multiple`,
        detachActionListItemFromAdmin: `/api/${API_VERSION}/action-list/detach`,
        detachActionListArrayFromAdmin: `/api/${API_VERSION}/action-list/detach-multiple`,
        moveActionListItemBetweenAdmin: `/api/${API_VERSION}/action-list/move`,
        moveMultipleActionListItemBetweenAdmin: `/api/${API_VERSION}/action-list/move-multiple`,
        updateActionListItemStatus: `/api/${API_VERSION}/action-list/status`,
    },
    // Utils endpoints
    utils: {
        getOccupationList: `/api/${API_VERSION}/utils/occupationList`,
        getRequirementsList: `/api/${API_VERSION}/utils/requirementsList`,
        getCriteriaCatalog: `/api/${API_VERSION}/utils/criteriaCatalog`,
        baseDataCriteriaCatalogList: `/api/${API_VERSION}/utils/baseDataCriteriaCatalogList`,
        getDownloadLink: `/api/${API_VERSION}/utils/getDownloadLink`,
        getBankInformations: `/api/${API_VERSION}/utils/getBankInformations`,
    },
    search: {
        getEvaluationList: `/api/${API_VERSION}/search/evaluation`,
        getCompanyAndFacilityList: `/api/${API_VERSION}/search/companyAndFacilityList`,
    },
    // Facility endpoints
    facilities: {
        getFacilityList: `/api/${API_VERSION}/facilities/list`,
        getFacilityListForCompany: `/api/${API_VERSION}/facilities/getFacilityListForCompany`,
        getSpecificFacility: `/api/${API_VERSION}/facilities/specific`,
        createFacility: `/api/${API_VERSION}/facilities/create`,
        updateFacility: `/api/${API_VERSION}/facilities/update`,
        editFacility: `/api/${API_VERSION}/facilities/edit`,
        setInternalNumber: `/api/${API_VERSION}/facilities/setInternalNumber`,
        delete: `/api/${API_VERSION}/facilities/delete`,
        getFacilitiesByAccount: `/api/${API_VERSION}/facilities/listByAccount`,
        moveFacilityToCompany: `/api/${API_VERSION}/facilities/moveFacilityToCompany`,
        makeFacilityIndependent: `/api/${API_VERSION}/facilities/makeFacilityIndependent`,
        createTermination: `/api/${API_VERSION}/facilities/createTermination`,
        createCancellation: `/api/${API_VERSION}/facilities/createCancellation`,
        createSuspension: `/api/${API_VERSION}/facilities/createSuspension`,
        suspensionPreview: `/api/${API_VERSION}/facilities/suspensionPreview`,
        createWithdrawal: `/api/${API_VERSION}/facilities/createWithdrawal`,
        close: `/api/${API_VERSION}/facilities/close`,
        reopen: `/api/${API_VERSION}/facilities/reopen`,
        deleteFacility: `/api/${API_VERSION}/facilities/deleteFacility`,
    },
    // Company endpoints
    company: {
        getCompany: `/api/${API_VERSION}/companies/get`,
        getMajorCustomerCompanyList: `/api/${API_VERSION}/companies/majorCustomerList`,
        getCompanyList: `/api/${API_VERSION}/companies/list`,
        getSpecificCompany: (id) => `/api/${API_VERSION}/companies/specific/${id}`,
        createCompany: `/api/${API_VERSION}/companies/create`,
        updateCompany: `/api/${API_VERSION}/companies/update`,
        editCompany: `/api/${API_VERSION}/companies/edit`,
        getContactPerson: `/api/${API_VERSION}/companies/getContactPerson`,
        changeMainHouse: `/api/${API_VERSION}/companies/changeMainHouse`,
    },
    // History endpoints
    history: {
        getFacilityHistory: `/api/${API_VERSION}/history/get`,
        createHistory: `/api/${API_VERSION}/history/create`,
        updateHistoryStatus: `/api/${API_VERSION}/history/update-status`,
        acceptHistoryFileUpload: `/api/${API_VERSION}/history/accept-file-upload`,
        deleteHistory: `/api/${API_VERSION}/history/delete`,
        editHistory: `/api/${API_VERSION}/history/edit`,
        editHistoryTime: `/api/${API_VERSION}/history/edit-time`,
        changeHistoryUser: `/api/${API_VERSION}/history/change-user`,
    },
    statistics: {
        customerStatistics: `/api/${API_VERSION}/statistics/customerStatistics`,
        facilitiesByScope: `/api/${API_VERSION}/statistics/facilitiesByScope`,
        getWalkerStats: `/api/${API_VERSION}/statistics/getWalkerStats`,
        getMonitoringStats: `/api/${API_VERSION}/statistics/getMonitoringStats`,
    },
    // Contact Person endpoints
    contactperson: {
        getContactPersonList: `/api/${API_VERSION}/contactpersons/list`,
        updateContactPerson: `/api/${API_VERSION}/contactpersons/update`,
        createContactPerson: `/api/${API_VERSION}/contactpersons/create`,
        deleteContactPerson: `/api/${API_VERSION}/contactpersons/delete`,
    },
    // Email endpoints
    email: {
        sendCustomEmail: `/api/${API_VERSION}/email/send-custom`,
        getEmailDetails: `/api/${API_VERSION}/email/get-eml`,
        sendMoreInfoEmail: `/api/${API_VERSION}/email/more-info`,
        sendDeadlineReminderEmail: `/api/${API_VERSION}/email/sendDeadlineReminderEmail`,
        sendMissingRequirementsEmail: `/api/${API_VERSION}/email/sendMissingRequirementsEmail`,
        sendMissingAssessmentRequirementsEmail: `/api/${API_VERSION}/email/sendMissingAssessmentRequirementsEmail`,
        sendIncompleteDocumentsEmail: `/api/${API_VERSION}/email/sendIncompleteDocumentsEmail`,
    },
    // Assessment endpoints
    assessment: {
        createAssessment: `/api/${API_VERSION}/assessment/create`,
        getAssessments: `/api/${API_VERSION}/assessment/get`,
        getSpecificAssessment: `/api/${API_VERSION}/assessment/specific`,
        rejectAssessment: `/api/${API_VERSION}/assessment/reject`,
        successAssessment: `/api/${API_VERSION}/assessment/success`,
        sendAssessmentData: `/api/${API_VERSION}/assessment/send`,
        getBillList: `/api/${API_VERSION}/assessment/bill-list`,
        getCertificate: `/api/${API_VERSION}/assessment/certificate`,
        createReview: `/api/${API_VERSION}/assessment/createReview`,
        getAllAssessments: `/api/${API_VERSION}/assessment/all`,
        facilityList: `/api/${API_VERSION}/assessment/facility-list`,
        refreshAssessmentItems: `/api/${API_VERSION}/assessment/refreshAssessmentItems`,
        combine: `/api/${API_VERSION}/assessment/combine`,
        changeStatus: `/api/${API_VERSION}/assessment/change-status`,
        changeActive: `/api/${API_VERSION}/assessment/change-active`,
        getOpenAssessmentsWithoutInspection: `/api/${API_VERSION}/assessment/open-without-inspection`,
        editChanges: `/api/${API_VERSION}/assessment/edit-changes`,
        merge: `/api/${API_VERSION}/assessment/merge`,
    },
    // Inspection endpoints
    inspection: {
        createInspection: `/api/${API_VERSION}/inspection/create`,
        getSpecificInspection: `/api/${API_VERSION}/inspection/specific`,
        getSpecificInspectionList: `/api/${API_VERSION}/inspection/specific-list`,
        getInspectionForFacility: `/api/${API_VERSION}/inspection/facility-list`,
        acceptInspection: `/api/${API_VERSION}/inspection/accept`,
        changeInspection: `/api/${API_VERSION}/inspection/change`,
        cancelInspection: `/api/${API_VERSION}/inspection/cancel`,
        updateDueDate: `/api/${API_VERSION}/inspection/update-due-date`,
        updateStatus: `/api/${API_VERSION}/inspection/update-status`,
        acceptInspectionCompletion: `/api/${API_VERSION}/inspection/accept-completion`,
        getInspectionRelevantProvidingFields: `/api/${API_VERSION}/inspection/relevant-fields`,
        getInspectionByPrequalification: `/api/${API_VERSION}/inspection/by-prequalification`,
        // Alias to match frontend apiService.js calls
        getInspectionByPrequalificationId: `/api/${API_VERSION}/inspection/by-prequalification`,
        resendAuditplan: `/api/${API_VERSION}/inspection/resend-auditplan`,
    },
    // The Begehungsprotokoll the walker fills in on site: the form (saved as a whole, with a
    // revision), photos per item, and filing it as a PDF under V130
    inspectionProtocol: {
        get: `/api/${API_VERSION}/inspection-protocol/get`,
        save: `/api/${API_VERSION}/inspection-protocol/save`,
        uploadPhoto: `/api/${API_VERSION}/inspection-protocol/upload-photo`,
        submit: `/api/${API_VERSION}/inspection-protocol/submit`,
    },
    // Specialist endpoints
    specialist: {
        createSpecialist: `/api/${API_VERSION}/specialist/create`,
        getSpecialist: `/api/${API_VERSION}/specialist/get`,
        getSpecialistList: `/api/${API_VERSION}/specialist/list`,
        deleteSpecialist: `/api/${API_VERSION}/specialist/delete`,
        moveSpecialist: `/api/${API_VERSION}/specialist/move`,
        editSpecialist: `/api/${API_VERSION}/specialist/edit`,
        getSpecificSpecialist: `/api/${API_VERSION}/specialist/specific`,
        updateSpecialist: `/api/${API_VERSION}/specialist/update`,
        updateOccupation: `/api/${API_VERSION}/specialist/updateOccupation`,
        checkSpecialistExistence: `/api/${API_VERSION}/specialist/check-existence`,
        addProvidingFields: `/api/${API_VERSION}/specialist/add-providing-fields`,
        connectToPrequalification: `/api/${API_VERSION}/specialist/connect-to-prequalification`,
        connectToFacility: `/api/${API_VERSION}/specialist/connect-to-facility`,
        disconnectFromPrequalification: `/api/${API_VERSION}/specialist/disconnect-from-prequalification`,
        updateSpecialistActiveState: `/api/${API_VERSION}/specialist/updateSpecialistActiveState`,
    },
    // Providing fields endpoints
    providingFields: {
        getProvidingField: `/api/${API_VERSION}/providing-fields/get`,
        getProvidingFieldList: `/api/${API_VERSION}/providing-fields/list`,
    },
    // invoice endpoints
    invoice: {
        get: `/api/${API_VERSION}/invoice/get`,
        list: `/api/${API_VERSION}/invoice/list`,
        createCollectiveBill: `/api/${API_VERSION}/invoice/create-collective-bill`,
        renewCollectiveBill: `/api/${API_VERSION}/invoice/renew-collective-bill`,
        sendCollectiveBillEmail: `/api/${API_VERSION}/invoice/send-collective-bill-email`,
        createInvoice: `/api/${API_VERSION}/invoice/create-invoice`,
        createYearlyBasicCollective: `/api/${API_VERSION}/invoice/create-yearly-basic-collective`,
        deleteBill: `/api/${API_VERSION}/invoice/delete-bill`,
        changeBillActiveState: `/api/${API_VERSION}/invoice/change-bill-active-state`,
        addInvoiceToCollectiveInvoice: `/api/${API_VERSION}/invoice/addInvoiceToCollectiveInvoice`,
        removeInvoiceFromCollectiveInvoice: `/api/${API_VERSION}/invoice/removeInvoiceFromCollectiveInvoice`,
        updateCollectiveInvoice: `/api/${API_VERSION}/invoice/update-collective-invoice`,
        transferInvoiceToCollective: `/api/${API_VERSION}/invoice/transfer-invoice-to-collective`,
        createStripeTaxRate: `/api/${API_VERSION}/invoice/createStripeTaxRate`,
        resetCollectiveBill: `/api/${API_VERSION}/invoice/reset-collective-bill`,
        getFacilityBillingStatus: `/api/${API_VERSION}/invoice/get-facility-billing-status`,
        getInvoiceImage: `/api/${API_VERSION}/invoice/get-invoice-image`,
    },
    // Prequalification endpoints
    prequalification: {
        // Standardized verbose frontend-style endpoint naming (PRIMARY)
        getPrequalificationDetails: `/api/${API_VERSION}/prequalification/get`,
        getFacilitySpecificPrequalifications: `/api/${API_VERSION}/prequalification/facilitySpecificList`,
        getFacilityPrequalifications: `/api/${API_VERSION}/prequalification/facilityList`,
        getPrequalificationList: `/api/${API_VERSION}/prequalification/list`,
        getSpecificPrequalificationItems: `/api/${API_VERSION}/prequalification/specific`,
        getSpecificPrequalificationItem: `/api/${API_VERSION}/prequalification/specificPrequalificationItem`,
        getSpecificPrequalificationItemList: `/api/${API_VERSION}/prequalification/specificItemList`, // FIXED: was incorrectly pointing to 'specific'
        requestPrequalification: `/api/${API_VERSION}/prequalification/request`,
        requestRePrequalification: `/api/${API_VERSION}/prequalification/requestRePrequalification`,
        updateCommentReadableStatus: `/api/${API_VERSION}/prequalification/changeCommentReadableStatus`,
        updatePreqComment: `/api/${API_VERSION}/prequalification/changePreqComment`,
        updateImageCommentReadableStatus: `/api/${API_VERSION}/prequalification/changeImageCommentReadableStatus`,
        updatePrequalificationCommentReadableStatus: `/api/${API_VERSION}/prequalification/changePreqCommentReadableStatus`,
        deletePrequalificationComment: `/api/${API_VERSION}/prequalification/deletePreqComment`,
        moveImageToPrequalificationItem: `/api/${API_VERSION}/prequalification/moveImage`,
        acceptPrequalificationRequest: `/api/${API_VERSION}/prequalification/acceptRequest`,
        rejectPrequalificationRequest: `/api/${API_VERSION}/prequalification/rejectRequest`,
        setPrequalificationForReview: `/api/${API_VERSION}/prequalification/pushToReview`,
        revertPrequalificationBack: `/api/${API_VERSION}/prequalification/failedReview`,
        rejectPrequalification: `/api/${API_VERSION}/prequalification/rejectPrequalification`,
        completePrequalificationSuccessfully: `/api/${API_VERSION}/prequalification/successPrequalification`,
        updateListItem: `/api/${API_VERSION}/prequalification/updateListItem`,
        updateItemActive: `/api/${API_VERSION}/prequalification/updateItemActive`,
        createPreqItemComment: `/api/${API_VERSION}/prequalification/createPreqItemComment`,
        createPreqItemImageComment: `/api/${API_VERSION}/prequalification/createPreqItemImageComment`,
        commentItemReadableUpdate: `/api/${API_VERSION}/prequalification/changeCommentReadableStatus`,
        commentItemImageReadableUpdate: `/api/${API_VERSION}/prequalification/changeImageCommentReadableStatus`,
        changeCriteriaCatalogVersion: `/api/${API_VERSION}/prequalification/changeCriteriaCatalogVersion`,
        revertCriteriaCatalogVersion: `/api/${API_VERSION}/prequalification/revertCriteriaCatalogVersion`,
        copyImageToFacility: `/api/${API_VERSION}/prequalification/copyImageToFacility`,
        updatePrequalificationDates: `/api/${API_VERSION}/prequalification/updatePrequalificationDates`,
        updatePrequalificationStatus: `/api/${API_VERSION}/prequalification/updatePrequalificationStatus`,
        refreshPrequalificationItems: `/api/${API_VERSION}/prequalification/refreshPrequalificationItems`,
        addPrequalificationItems: `/api/${API_VERSION}/prequalification/addItems`,
        deletePrequalificationItem: `/api/${API_VERSION}/prequalification/deleteItem`,
        updatePrequalificationActiveState: `/api/${API_VERSION}/prequalification/updatePrequalificationActiveState`,
        // Aliases for backward compatibility with direct API access (SECONDARY)
        // These are concise names that map to the backend routes
        get: `/api/${API_VERSION}/prequalification/get`,
        facilitySpecificList: `/api/${API_VERSION}/prequalification/facilitySpecificList`,
        facilityList: `/api/${API_VERSION}/prequalification/facilityList`,
        list: `/api/${API_VERSION}/prequalification/list`,
        specific: `/api/${API_VERSION}/prequalification/specific`,
        specificItemList: `/api/${API_VERSION}/prequalification/specificItemList`,
        request: `/api/${API_VERSION}/prequalification/request`,
        changeCommentReadableStatus: `/api/${API_VERSION}/prequalification/changeCommentReadableStatus`,
        changeImageCommentReadableStatus: `/api/${API_VERSION}/prequalification/changeImageCommentReadableStatus`,
        changePreqCommentReadableStatus: `/api/${API_VERSION}/prequalification/changePreqCommentReadableStatus`,
        deletePreqComment: `/api/${API_VERSION}/prequalification/deletePreqComment`,
        moveImage: `/api/${API_VERSION}/prequalification/moveImage`,
        acceptRequest: `/api/${API_VERSION}/prequalification/acceptRequest`,
        rejectRequest: `/api/${API_VERSION}/prequalification/rejectRequest`,
        pushToReview: `/api/${API_VERSION}/prequalification/pushToReview`,
        failedReview: `/api/${API_VERSION}/prequalification/failedReview`,
        successPrequalification: `/api/${API_VERSION}/prequalification/successPrequalification`,
        declineRequest: `/api/${API_VERSION}/prequalification/declineRequest`,
        // Legacy duplicates with different names - maintained for compatibility
        updateCommentItemReadableStatus: `/api/${API_VERSION}/prequalification/changeCommentReadableStatus`,
        updateCommentItemImageReadableStatus: `/api/${API_VERSION}/prequalification/changeImageCommentReadableStatus`,
    },
    // Notes endpoints
    notes: {
        getNotes: `/api/${API_VERSION}/notes/getNotes`,
        createNotes: `/api/${API_VERSION}/notes/createNotes`,
        updateNotes: `/api/${API_VERSION}/notes/updateNotes`,
        deleteNotes: `/api/${API_VERSION}/notes/deleteNotes`,
        updateNoteStatus: `/api/${API_VERSION}/notes/updateNoteStatus`,
    },
    // Changes endpoints
    changes: {
        requestChangesWithImpactOnCertificate: `/api/${API_VERSION}/changes/requestWithImpactOnCertificate`,
        createChange: `/api/${API_VERSION}/changes/createChange`,
        changeOfCEO: `/api/${API_VERSION}/changes/changeOfCEO`,
        openInsolvency: `/api/${API_VERSION}/changes/openInsolvency`,
        closeInsolvency: `/api/${API_VERSION}/changes/closeInsolvency`,
        closeCompany: `/api/${API_VERSION}/changes/closeCompany`,
        // Specialist-related change endpoints
        specialist: {
            addSpecialist: `/api/${API_VERSION}/changes/specialist/addSpecialist`,
            changeSpecialist: `/api/${API_VERSION}/changes/specialist/changeSpecialist`,
            acceptDeleteSpecialist: `/api/${API_VERSION}/changes/specialist/acceptDeleteSpecialist`,
            rejectDeleteSpecialist: `/api/${API_VERSION}/changes/specialist/rejectDeleteSpecialist`,
        },
        // Contact person change endpoints
        contactPerson: {
            addContactPerson: `/api/${API_VERSION}/changes/contactPerson/addCP`,
            changeContactPerson: `/api/${API_VERSION}/changes/contactPerson/change`,
            changeBankCredentials: `/api/${API_VERSION}/changes/contactPerson/changeBankCredentials`,
            changeBillingAddress: `/api/${API_VERSION}/changes/contactPerson/changeBillingAddress`,
            changeBillingEmail: `/api/${API_VERSION}/changes/contactPerson/changeBillingEmail`,
            changeContactPersonEmail: `/api/${API_VERSION}/changes/contactPerson/changeCPEmail`,
            removeContactPerson: `/api/${API_VERSION}/changes/contactPerson/removeCP`,
        },
    },
    // Media operations endpoints
    media: {
        uploadGenericFile: `/api/${API_VERSION}/media/upload`,
        uploadWalkerFile: `/api/${API_VERSION}/media/uploadWalker`,
        uploadCloseCompany: `/api/${API_VERSION}/media/uploadCloseCompany`,
        uploadAssessmentFile: `/api/${API_VERSION}/media/uploadAssessment`,
        uploadHistoryFile: `/api/${API_VERSION}/media/uploadHistory`,
        getS3Object: `/api/${API_VERSION}/media/getS3Object`,
        getSpecificFile: `/api/${API_VERSION}/media/getSpecificFile`,
        updateFileName: `/api/${API_VERSION}/media/updateFileName`,
        updateImage: `/api/${API_VERSION}/media/updateImage`,
        rotateImage: `/api/${API_VERSION}/media/rotateImage`,
        deleteFile: `/api/${API_VERSION}/media/delete`,
        getBillList: `/api/${API_VERSION}/media/billList`,
        getMajorCustomerBillList: `/api/${API_VERSION}/media/majorCustomerBillList`,
        renewBill: `/api/${API_VERSION}/media/renewBill`,
        sendBillEmail: `/api/${API_VERSION}/media/sendBillEmail`,
        getBillsByDateRange: `/api/${API_VERSION}/media/billsByDateRange`,
        getBillsByEntityId: `/api/${API_VERSION}/media/billsByEntityId`,
        getBillsByEntityAndDateRange: `/api/${API_VERSION}/media/billsByEntityAndDateRange`,
        updateCompanyBillingList: `/api/${API_VERSION}/media/updateCompanyBillingList`,
        updateBilllistByDateRange: `/api/${API_VERSION}/media/updateBilllistByDateRange`,
        getCertificate: `/api/${API_VERSION}/media/certificate`,
        createNewCertificate: `/api/${API_VERSION}/media/createNewCertificate`,
        createPreviewCertificate: `/api/${API_VERSION}/media/createPreviewCertificate`,
        createPreviewCollectiveBill: `/api/${API_VERSION}/media/createPreviewCollectiveBill`,
        createStatusReport: `/api/${API_VERSION}/media/createStatusReport`,
        createPreviewXML: `/api/${API_VERSION}/media/createPreviewXML`,
        updateFileStatus: `/api/${API_VERSION}/media/updateFileStatus`,
        updateFileSubmitted: `/api/${API_VERSION}/media/updateFileSubmitted`,
        getUploadQueueStatus: `/api/${API_VERSION}/media/queue-status`,
    },
    xml: {
        getList: `/api/${API_VERSION}/xml/getList`,
        getXMLContent: `/api/${API_VERSION}/xml/getXMLContent`,
        downloadXML: `/api/${API_VERSION}/xml/downloadXML`,
        createPreviewXML: `/api/${API_VERSION}/xml/createPreviewXML`,
        createCorrectionXML: `/api/${API_VERSION}/xml/createCorrectionXML`,
        createInitialXML: `/api/${API_VERSION}/xml/createInitialXML`,
        extractXMLContent: `/api/${API_VERSION}/xml/extractXMLContent`,
        createWithdrawalXML: `/api/${API_VERSION}/xml/createWithdrawalXML`,
        getFacilityXMLList: `/api/${API_VERSION}/xml/getFacilityXMLList`,
        // Staff send a report the GKV doesn't have again
        resendReport: `/api/${API_VERSION}/xml/resendReport`,
        // A customer's view: its facility's sent reports, and one of them with its file
        sentReports: `/api/${API_VERSION}/xml/sentReports`,
        sentReport: `/api/${API_VERSION}/xml/sentReport`,
    },
};
/**
 * Get the base API URL based on the current environment
 */
export function getApiBaseUrl(config) {
    const env = process.env.NODE_ENV || "development";
    switch (env) {
        case "production":
            return config.production;
        case "test":
            return config.test || config.development;
        default:
            return config.development;
    }
}
